import { defineStore } from 'pinia';
import {
  computed,
  ref,
} from 'vue';

import { accountApiConfig } from '@/config/api/account';
import { documentsApiConfig } from '@/config/api/documents';
import { useClientApi } from '@/plugins/api/lk';
import { useUserRole } from '@/shared/composables';
import {
  decorateApiActions,
  fullLog,
} from '@/shared/lib';
import { openLinkInNewTab } from '@/shared/lib/utils/openLinkInNewTab';
import type { Agency } from '@/types/Agencies';
import type {
  Agreement,
  PreparedAgreement,
} from '@/types/Agreements';
import type {
  ContractData,
  Project,
  Type,
} from '@/types/Contract';
import type { PageInfo } from '@/types/PageInfo';
import type { TableHeading } from '@/types/Table';

import {
  contractHeadingList,
  defaultSteps,
  documentErrorMessage,
  notifyRethrowDecorate,
  organisationErrorMessage,
  preparedContracts,
} from './lib';

import type {
  AgreementsResponse,
  NextPagePayload,
  Step,
} from './types';

export const useContractStore = defineStore('contract', () => {
  const steps = ref<Array<Step>>(defaultSteps);
  const contractData = ref<ContractData>({
    isInformationFilled: false,
    isComplexChosen: false,
    isTypeChosen: false,
  });
  const currentStep = ref<Step | undefined>(defaultSteps[0]);
  const currentComplex = ref<Array<number>>([]);
  const types = ref<Array<Type>>([]);
  const currentType = ref<Type | undefined>(undefined);
  const currentAgreement = ref<Agreement | undefined>(undefined);
  const agreements = ref<Array<PreparedAgreement>>([]);
  const newContracts = ref<Array<PreparedAgreement>>([]);
  const count = ref(0);
  const pageInfo = ref<PageInfo | object>({});

  const getHeadingList = computed<Array<TableHeading>>(() => contractHeadingList);

  async function getOrganisationData (): Promise<Agency> {
    const { role } = useUserRole();
    const {
      agencyGetContractData,
    } = accountApiConfig.agencies;
    const url = agencyGetContractData(role);
    const {
      data,
    } = await useClientApi().get<Agency>(url);

    return data;
  }

  async function updateOrganisationData (payload: object): Promise<void> {
    const { role } = useUserRole();
    const {
      agencyFillContractData,
    } = accountApiConfig.agencies;
    const url = agencyFillContractData(role);

    await useClientApi().patch(url, payload);
  }

  function setComplex (payload: Array<Project>): void {
    currentComplex.value = payload
      .filter(({ active }) => active)
      .map(({ id }) => id);
  }

  async function getTypes (): Promise<void> {
    const url = accountApiConfig.agencies.getAgreementTypes;
    const {
      data,
    } = await useClientApi().get<Array<Type>>(url);

    types.value = data;
  }

  function setType (payload: Type): void {
    currentType.value = payload;
  }

  async function getAgreements (params: object): Promise<void> {
    const { role } = useUserRole();
    const {
      agreements: agreementsUrl,
    } = accountApiConfig.agencies;
    const url = agreementsUrl(role);
    const {
      data,
    } = await useClientApi().get<AgreementsResponse>(url, {
      params,
    });

    agreements.value = preparedContracts(data.result);
    count.value = data.count;
    pageInfo.value = data.page_info;
  }

  async function nextPage ({ page, infiniteState }: NextPagePayload): Promise<void> {
    try {
      if (!page.next_page) {
        return;
      }

      const {
        data: {
          result,
          page_info: nextPageInfo,
        },
      } = await useClientApi().get<AgreementsResponse>(page.next_page);

      if (!nextPageInfo) {
        throw new Error('page info not received');
      }

      if (!result?.length) {
        infiniteState.loaded();

        return;
      }

      pageInfo.value = nextPageInfo;
      agreements.value = [
        ...agreements.value,
        ...preparedContracts(result),
      ];

      if (nextPageInfo.next_page) {
        infiniteState.loaded();
      } else {
        infiniteState.complete();
      }
    } catch (error: unknown) {
      fullLog(error, '🚀 ~ file: contract.ts ~ nextPage ~ error');
      infiniteState.complete();
    }
  }

  async function createAgreements (): Promise<void> {
    const { role } = useUserRole();
    const {
      agreements: agreementsUrl,
    } = accountApiConfig.agencies;
    const url = agreementsUrl(role);
    const body = {
      projects: currentComplex.value,
      type_id: currentType.value?.id,
    };
    const {
      data,
    } = await useClientApi().post<Array<Agreement>>(url, body);

    newContracts.value = preparedContracts(data);
  }

  async function getAgreement (id: number): Promise<void> {
    const { role } = useUserRole();
    const {
      agreement: agreementUrl,
    } = accountApiConfig.agencies;
    const url = agreementUrl(role, id);
    const {
      data,
    } = await useClientApi().get<Agreement>(url);

    currentAgreement.value = data;
  }

  function setCurrentStep (payload: string): void {
    currentStep.value = steps.value.find(
      ({ component }) => component === payload,
    );
  }

  async function getAgreementDocument (id: number): Promise<void> {
    const { role } = useUserRole();
    const {
      getDocument,
    } = documentsApiConfig;
    const url = getDocument(role, id);
    const {
      data,
    } = await useClientApi().get<Agreement>(url);
    const link = data.files?.[0]?.files?.[0]?.aws;

    if (!link) {
      throw new Error(documentErrorMessage);
    }

    openLinkInNewTab(link);
  }

  const decoratedApiActions = decorateApiActions({
    getTypes,
    getAgreements,
  }, 'contract.ts');

  const decoratedRethrowActions = decorateApiActions({
    getAgreement,
  }, 'contract.ts', true);

  const loggedNotifyingActions = decorateApiActions({
    getOrganisationData,
    updateOrganisationData,
    createAgreements,
    getAgreementDocument,
  }, 'contract.ts', true);

  const notifyingActions = {
    getOrganisationData: notifyRethrowDecorate(loggedNotifyingActions.getOrganisationData, organisationErrorMessage),
    updateOrganisationData: notifyRethrowDecorate(loggedNotifyingActions.updateOrganisationData),
    createAgreements: notifyRethrowDecorate(loggedNotifyingActions.createAgreements),
    getAgreementDocument: notifyRethrowDecorate(loggedNotifyingActions.getAgreementDocument, documentErrorMessage),
  };

  return {
    steps,
    contractData,
    currentStep,
    currentComplex,
    types,
    currentType,
    currentAgreement,
    agreements,
    newContracts,
    count,
    pageInfo,
    getHeadingList,
    setComplex,
    setType,
    setCurrentStep,
    nextPage,
    ...decoratedApiActions,
    ...decoratedRethrowActions,
    ...notifyingActions,
  };
});
